// RC6.2 Slice 5 (B3) — pure, framework-free helpers for the Deliveries receive workflow.
// AI output is never authoritative here: these helpers only ever map an AI PROPOSAL into editable
// human fields and compute gating for the single human confirmation.

export const ACCEPTANCE = ["accepted", "rejected"];

export const DOC_TYPES = [
  { v: "invoice", l: "Invoice" },
  { v: "delivery_note", l: "Delivery note" },
  { v: "other", l: "Other" },
];

// good:true → ticking it is the SAFE state; good:false → ticking it flags a problem.
export const CHECK_ITEMS = [
  { k: "packaging_ok", l: "Packaging intact", good: true },
  { k: "labels_dates_ok", l: "Labels & use-by dates OK", good: true },
  { k: "damage", l: "Visible damage", good: false },
  { k: "contamination", l: "Signs of contamination", good: false },
];

const _n = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));

// null = not yet entered; true/false = within/outside the configured range.
export function tempPassed(t) {
  const v = _n(t.temperature);
  if (v === null) return null;
  const mn = _n(t.minimum), mx = _n(t.maximum);
  if (mn !== null && v < mn) return false;
  if (mx !== null && v > mx) return false;
  return true;
}

export function hasException(temps = []) {
  return temps.some((t) => tempPassed(t) === false);
}

export function needsCorrectiveAction(acceptance, temps = []) {
  return acceptance === "rejected" || hasException(temps);
}

// Map a validated (but still UNTRUSTED) AI proposal into editable header fields. Never touches
// temperatures — a document temperature is not a food-safety reading.
export function mapAiProposalToReference(p = {}) {
  return {
    supplier_name: p.supplier_name || "",
    document_number: p.delivery_number || p.invoice_number || "",
    document_date: p.delivery_date || p.invoice_date || "",
  };
}

// A temperature printed on the document — reference text ONLY, shown to the human, never saved as
// an observed reading.
export function documentTemperatureNote(p = {}) {
  return typeof p.temperature_c === "number" ? p.temperature_c : null;
}

export function acceptanceLabel(a) {
  return a === "accepted" ? "Accepted" : a === "rejected" ? "Rejected" : a;
}

// Server-mirrored gating for the single human confirmation.
export function canConfirm({ acceptance, supplierName, temps = [], correctiveAction = "" }) {
  if (!ACCEPTANCE.includes(acceptance)) return false;
  if (!supplierName || !supplierName.trim()) return false;
  for (const t of temps) {
    if (_n(t.temperature) === null) return false;
    if (tempPassed(t) === false && !(t.comments && t.comments.trim())) return false;
  }
  if (needsCorrectiveAction(acceptance, temps) && !(correctiveAction && correctiveAction.trim())) return false;
  return true;
}

export const TEMP_PRESETS = [
  { label: "Chilled", minimum: 0, maximum: 5 },
  { label: "Frozen", minimum: -25, maximum: -18 },
  { label: "Hot", minimum: 63, maximum: "" },
];

// RC6.2.2 — map extracted (untrusted) line items into editable, human-confirmable learning lines.
// Default action is to create a new canonical product; the user can switch a line to an existing
// product or leave it unlinked before confirming. Nothing here is authoritative.
const _num2 = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : null);
// Product name as printed (the AI's `name`); `description` can hold the second printed line (codes, case qty).
// Dot leaders ('Oil. . LA') are layout, not part of the name.
const rowText = (v) => (v || "").toString().replace(/(\s*\.){2,}\s*/g, " ").replace(/\s*\.+\s*$/, "").trim();
const descFirst = (li) => { const n = rowText(li.name).toLowerCase(), d = rowText(li.description).toLowerCase(); return !!d && (!n || d.includes(n)); };
const rowName = (li) => rowText(descFirst(li) ? li.description : li.name).slice(0, 300);
export function mapProposalLines(p = {}) {
  const items = Array.isArray(p.line_items) ? p.line_items : [];
  return items.slice(0, 200).map((li) => {
    const notDelivered = !!li.out_of_stock;
    return {
    canonical_name: rowName(li),
    description: rowName(li),
    printed_detail: !descFirst(li) && li.description && li.description !== li.name ? li.description.toString().slice(0, 200) : null,
    sku: (li.sku || "").toString().slice(0, 80),
    pack_size: (li.pack_size || "").toString().slice(0, 80),
    unit: (li.unit || "").toString().slice(0, 40),
    price: notDelivered ? null : _num2(li.unit_price),
    vat_rate: _num2(li.vat_rate) ?? _num2(li.derived?.vat_rate_from_legend),
    // Delivered quantity is never filled from the ordered quantity: when a separate ordered column was read
    // but no delivered value, the delivered quantity stays empty for the chef to enter.
    quantity: notDelivered ? 0 : li.qty_delivered != null ? _num2(li.qty_delivered) : (li.qty_ordered != null ? null : _num2(li.quantity)),
    qty_ordered: _num2(li.qty_ordered),
    not_delivered: notDelivered,
    // Extracted (as printed) vs derived (calculated) facts, shown separately to the reviewer.
    printed: { weight: _num2(li.weight), weight_unit: li.weight_unit || null, price_basis: li.price_basis || null,
      vat_code: li.vat_code || li.printed_text?.vat_rate || null, price_marker: li.price_marker || null, line_net: _num2(li.line_net), page: _num2(li.page) },
    derived: li.derived || null,
    line_status: li.line_status || null,
    handwritten_note: li.handwritten_note || null,
    review: (li.uncertain_fields?.length || li.conflicts || li.crop_file_id || li.line_status || li.handwritten_note || li.review_reasons?.length)
      ? { uncertain: li.uncertain_fields || [], conflicts: li.conflicts || null, alternatives: li.alternatives || null,
          crop_file_id: li.crop_file_id || null, recovered: li.recovered_fields || [], reread: !!li.reread, reasons: li.review_reasons || [],
          price_reference: li.price_reference || null }
      : null,
    product_id: null,        // set when the user matches to an existing product
    create_product: !notDelivered, // default: learn as a new canonical product on confirm (never for undelivered lines)
    };
  });
}

// Build the human-confirmed learning payload sent with the delivery. Supplier/product/global-supplier
// resources are learned ONLY from this payload — never from the AI proposal alone.
export function buildLearnPayload({ supplierId, supplierName, lines = [], learnGlobal = true }) {
  return {
    supplier: {
      supplier_id: supplierId || undefined,
      name: (supplierName || "").trim(),
      create_supplier: !supplierId && !!(supplierName || "").trim(),
      create_global: !supplierId && learnGlobal && !!(supplierName || "").trim(),
      global: {},
    },
    lines: lines.filter((l) => !l.not_delivered).map((l) => ({
      product_id: l.product_id || undefined,
      create_product: !l.product_id && l.create_product !== false,
      canonical_name: l.canonical_name,
      description: l.description,
      sku: l.sku,
      pack_size: l.pack_size,
      unit: l.unit,
      price: l.price,
      vat_rate: l.vat_rate,
      not_delivered: !!l.not_delivered || undefined,
    })),
  };
}

// Concise "what CaterCTRL will learn" summary for the final review.
export function learnSummaryText({ supplierId, supplierName, lines = [] }) {
  const parts = [];
  if (supplierId) parts.push(`link supplier: ${supplierName}`);
  else if ((supplierName || "").trim()) parts.push(`add supplier: ${supplierName}`);
  const linked = lines.filter((l) => l.product_id && !l.not_delivered).length;
  const created = lines.filter((l) => !l.product_id && l.create_product !== false && !l.not_delivered).length;
  if (linked) parts.push(`link ${linked} existing product${linked > 1 ? "s" : ""}`);
  if (created) parts.push(`create ${created} new product${created > 1 ? "s" : ""}`);
  const priced = lines.filter((l) => typeof l.price === "number" && !l.not_delivered).length;
  if (priced) parts.push(`record ${priced} purchase price${priced > 1 ? "s" : ""}`);
  return parts;
}


// Safe, specific message for a failed AI extraction (server message + short support reference).
export function describeExtractError(norm) {
  const n = norm || {};
  let message = n.message || "AI couldn't read the document — enter the details manually.";
  if (n.status === 0) message = "No connection to CaterCTRL — check your signal and try again, or enter the details manually.";
  else if (n.status === 401) message = "Your session has expired — sign in again, then retry the extraction.";
  else if (n.status === 413) message = "That photo is too large to send — retake it or enter the details manually.";
  else if (n.status >= 500 && !n.code) message = "AI reading failed on the server — try again shortly or enter the details manually.";
  return { message, code: n.code || null, status: n.status ?? null, reference: n.reference || null };
}
